import React, { Suspense } from "react";
import {
  usePreloadedQuery,
  useQueryLoader,
  graphql,
  PreloadedQuery,
} from "react-relay/hooks";
import NotificationBadge from "@components/atoms/NotificationBadge";
import NotificationBell from "@components/organisms/NotificationBell";
import { navigate } from "@navigation/navigate";
import { TouchableOpacity } from "react-native";
import { HomeNotificationBellQuery } from "@generated/HomeNotificationBellQuery.graphql";
import _ from "lodash";

const query = graphql`
  query HomeNotificationBellQuery {
    notificationUnreadTotal {
      ...NotificationBell_total
    }
  }
`;

// 未読数取得前 / 取得失敗時に表示するバッジなしのベル。
function FallbackBell(): JSX.Element {
  return (
    <TouchableOpacity
      accessibilityLabel="お知らせ"
      accessibilityRole="button"
      hitSlop={{ top: 8, right: 8, bottom: 8, left: 8 }}
      onPress={() => navigate("Notifications")}
    >
      <NotificationBadge count={0} size={24} />
    </TouchableOpacity>
  );
}

function BellContent({
  queryReference,
}: {
  queryReference: PreloadedQuery<HomeNotificationBellQuery>;
}): JSX.Element {
  const { notificationUnreadTotal } =
    usePreloadedQuery<HomeNotificationBellQuery>(query, queryReference);
  if (notificationUnreadTotal == null) {
    return <FallbackBell />;
  }
  return <NotificationBell fragmentRef={notificationUnreadTotal} />;
}

export default function HomeNotificationBell(): JSX.Element {
  const [queryReference, loadQuery, disposeQuery] =
    useQueryLoader<HomeNotificationBellQuery>(query);
  React.useEffect(() => {
    loadQuery({}, { fetchPolicy: "network-only" });
    return () => {
      disposeQuery();
    };
  }, [loadQuery, disposeQuery]);

  return (
    <Suspense fallback={<FallbackBell />}>
      {!_.isNil(queryReference) && (
        <BellContent queryReference={queryReference} />
      )}
    </Suspense>
  );
}
